import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { LogOut, ChevronDown, ChevronRight, KeyRound, LayoutGrid, Shield, BookOpen, MonitorX } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { endSession } from "@/lib/session";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import logo from "@/assets/ffs-logo-horizontal.svg";
import BrandLogo from "@/components/BrandLogo";
import { PersonaToggle } from "@/components/PersonaToggle";
import { PageHelpLink } from "@/components/PageHelpLink";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { TrustedDevicesDialog } from "@/components/TrustedDevicesDialog";
import { currentModule, moduleItems } from "@/lib/modules";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { NoContactBanner } from "@/components/NoContactBanner";
import { SupportBanner } from "@/components/SupportBanner";

const CRIMSON = "var(--brand)";

// Build an ordered list of groups + their items from a flat items array.
// Items without a group property are returned as a single ungrouped bucket (key null).
function buildGroups(items) {
  const map = new Map(); // preserves insertion order
  for (const item of items) {
    const key = item.group ?? null;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  return Array.from(map.entries()).map(([group, groupItems]) => ({ group, items: groupItems }));
}

export function AppShell() {
  const { user, logout, can } = useAuth();
  // For when someone may have used your account: ends every session, this one included.
  const signOutEverywhere = async () => {
    if (!window.confirm("Sign out on every device, including this one?")) return;
    try { await api.signOutEverywhere(); endSession("signed_out_everywhere"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't sign out everywhere. Please try again."); }
  };
  const roleLabels = user?.role_labels?.length ? user.role_labels : [user?.role];
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [pwdOpen, setPwdOpen] = useState(false);
  const [trustedDevicesOpen, setTrustedDevicesOpen] = useState(false);
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const onLauncher = pathname === "/" || pathname === "/help" || pathname.startsWith("/help/");
  const mod = onLauncher ? null : currentModule(pathname, can);
  const items = mod ? moduleItems(mod, can) : [];
  const ModIcon = mod?.icon;

  const groups = useMemo(() => buildGroups(items), [items]);
  const hasGroups = groups.some((g) => g.group !== null);

  // Find which group (if any) contains the active route
  const activeGroup = useMemo(() => {
    if (!hasGroups) return null;
    for (const { group, items: gItems } of groups) {
      if (group && gItems.some((it) => pathname === it.to || pathname.startsWith(it.to + "/"))) {
        return group;
      }
    }
    return null;
  }, [groups, hasGroups, pathname]);

  // Only the active group is open by default; all others collapsed
  const [openGroups, setOpenGroups] = useState(() => (activeGroup ? new Set([activeGroup]) : new Set()));

  // When navigating to a page whose group is currently closed, auto-open it
  useEffect(() => {
    if (activeGroup) {
      setOpenGroups((prev) => {
        if (prev.has(activeGroup)) return prev;
        const next = new Set(prev);
        next.add(activeGroup);
        return next;
      });
    }
  }, [activeGroup]);

  const toggleGroup = (group) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group); else next.add(group);
      return next;
    });
  };

  return (
    <div className="min-h-screen font-body" style={{ backgroundColor: "#fcfcfc" }}>
      <NoContactBanner />
      <SupportBanner />
      <header className="h-14 bg-white border-b border-slate-200 px-4 flex items-center justify-between sticky top-0 z-30 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/")} data-testid="apps-home" className="shrink-0">
            <BrandLogo fallback={logo} className="h-8 w-auto" data-testid="app-logo" />
          </button>
          {!onLauncher && mod && (
            <>
              <button
                onClick={() => nav("/")}
                data-testid="apps-grid-btn"
                title="All modules"
                className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition-colors"
              >
                <LayoutGrid className="h-4 w-4" /> Apps
              </button>
              <span className="text-slate-300">/</span>
              <span className="text-sm font-semibold flex items-center gap-1.5" style={{ color: mod.accent }} data-testid="current-module">
                {ModIcon && <ModIcon className="h-4 w-4" />}{mod.label}
              </span>
            </>
          )}
          <PersonaToggle variant="light" />
        </div>
        <div className="flex items-center gap-2">
        <PageHelpLink basePath="/help" iconClassName="h-4 w-4"
          className="h-8 w-8 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-slate-50">
              {user?.photo
                ? <img src={user.photo} alt={user?.name || "Profile"} data-testid="user-avatar-photo" className="h-8 w-8 rounded-full object-cover" />
                : <div className="h-8 w-8 rounded-full text-white flex items-center justify-center text-[11px] font-semibold" style={{ backgroundColor: CRIMSON }}>{initials}</div>}
              <div className="text-left leading-tight hidden sm:block">
                <div className="text-xs font-semibold text-slate-800">{user?.name}</div>
                <div className="text-[10px] text-slate-500 -mt-0.5">{roleLabels[0]}{roleLabels.length > 1 ? ` +${roleLabels.length - 1}` : ""}</div>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs">
              <div className="font-semibold">{user?.username}</div>
              <div className="text-[10px] text-slate-500 font-normal mt-0.5">{roleLabels.length > 1 ? "Roles" : "Role"}: {roleLabels.join(", ")}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav("/help")} data-testid="help-menu-item" className="text-xs">
              <BookOpen className="h-3.5 w-3.5 mr-2" /> Help &amp; guides
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setPwdOpen(true)} data-testid="change-password-button" className="text-xs">
              <KeyRound className="h-3.5 w-3.5 mr-2" /> Change password
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTrustedDevicesOpen(true)} data-testid="trusted-devices-button" className="text-xs">
              <Shield className="h-3.5 w-3.5 mr-2" /> Trusted devices
            </DropdownMenuItem>
            <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-xs text-red-600">
              <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
            </DropdownMenuItem>
            <DropdownMenuItem onClick={signOutEverywhere} data-testid="sign-out-everywhere-button" className="text-xs text-red-600">
              <MonitorX className="h-3.5 w-3.5 mr-2" /> Sign out everywhere
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </header>

      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      <TrustedDevicesDialog open={trustedDevicesOpen} onOpenChange={setTrustedDevicesOpen} />

      {onLauncher ? (
        <main className="min-w-0"><ErrorBoundary><Outlet /></ErrorBoundary></main>
      ) : (
        <div className="flex">
          <aside className="w-56 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-3.5rem)] p-2 hidden md:block">
            <nav className="space-y-0.5">
              {hasGroups ? (
                groups.map(({ group, items: gItems }, gi) => {
                  if (!group) {
                    // Ungrouped items — render flat (safe fallback)
                    return gItems.map((n) => (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                            isActive ? "font-semibold" : "text-slate-600 hover:bg-slate-50"
                          }`
                        }
                        style={({ isActive }) => (isActive ? { backgroundColor: "var(--brand-soft)", color: CRIMSON } : undefined)}
                      >
                        <n.icon className="h-4 w-4" />
                        {n.label}
                      </NavLink>
                    ));
                  }
                  const isOpen = openGroups.has(group);
                  const groupHasActive = gItems.some((it) => pathname === it.to || pathname.startsWith(it.to + "/"));
                  return (
                    <div key={group} className={gi > 0 ? "mt-1" : ""}>
                      <button
                        onClick={() => toggleGroup(group)}
                        className={`w-full flex items-center justify-between rounded-lg px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                          groupHasActive
                            ? "text-slate-700 bg-slate-100"
                            : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        {group}
                        {isOpen
                          ? <ChevronDown className="h-3 w-3 shrink-0" />
                          : <ChevronRight className="h-3 w-3 shrink-0" />
                        }
                      </button>
                      {isOpen && (
                        <div className="mt-0.5 space-y-0.5">
                          {gItems.map((n) => (
                            <NavLink
                              key={n.to}
                              to={n.to}
                              end={n.end}
                              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                              className={({ isActive }) =>
                                `flex items-center gap-2.5 rounded-lg pl-5 pr-3 py-2 text-xs font-medium transition-colors ${
                                  isActive ? "font-semibold" : "text-slate-600 hover:bg-slate-50"
                                }`
                              }
                              style={({ isActive }) => (isActive ? { backgroundColor: "var(--brand-soft)", color: CRIMSON } : undefined)}
                            >
                              <n.icon className="h-4 w-4 shrink-0" />
                              {n.label}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                        isActive ? "font-semibold" : "text-slate-600 hover:bg-slate-50"
                      }`
                    }
                    style={({ isActive }) => (isActive ? { backgroundColor: "var(--brand-soft)", color: CRIMSON } : undefined)}
                  >
                    <n.icon className="h-4 w-4" />
                    {n.label}
                  </NavLink>
                ))
              )}
            </nav>
            <div className="mt-4 px-3 py-2 rounded-lg bg-emerald-50/60 border border-emerald-100">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700/70 mb-1">Your data</div>
              <div className="text-[10px] text-slate-500 leading-snug">Stored securely in Australia — private and compliant.</div>
            </div>
          </aside>

          <main className="flex-1 min-w-0">
            <ErrorBoundary><Outlet /></ErrorBoundary>
          </main>
        </div>
      )}
    </div>
  );
}
